import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Printer, Download, FileText, Loader2, AlertCircle } from 'lucide-react';
import notaCompraTemplateHtml from './notaCompraTemplate.js';

const NotaCompraPrintModal = ({ compra, onClose, onImprimir }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const [notaData, setNotaData] = useState(null);
  
  // El template HTML se importa desde el archivo JS
  const htmlContent = notaCompraTemplateHtml;

  useEffect(() => {
    if (!compra) return;
    
    try {
      setLoading(true);
      
      // Formatear los datos de la compra para el template de Nota de Entrega
      const esInternacional = compra.tipo === 'internacional';
      const fechaActual = new Date();
      const fechaStr = fechaActual.toLocaleDateString('es-VE');
      
      const formattedData = {
        // Encabezado
        numero: compra.id || 'N/A',
        fecha: fechaStr,
        
        // Info adicional
        tipo: esInternacional ? '🌍 Internacional' : '🏠 Nacional',
        cotizacion_origen: compra.cotizacion_origen || 'N/A',
        prioridad: (compra.prioridad || 'media').toUpperCase(),
        
        // Item entregado (tabla)
        cantidad: compra.cantidad || 1,
        descripcion: compra.producto || 'Sin descripción',
        factura: compra.factura || '-', // Número de factura ingresado en edición
        gid: compra.cotizacion_origen || '-',
        empresa: compra.cliente_nombre || '-',
        
        // Tracking
        tracking_comentario: compra.tracking_comentario || '',
        
        // Observaciones (notas de la compra)
        observaciones: compra.notas || '',
        
        // Firmas (cargar desde compra si existen, sino dejar vacío para llenar manual)
        receiver_name: compra.receiver_name || '',
        receiver_id: compra.receiver_id || '',
        deliverer_name: compra.deliverer_name || '',
        deliverer_id: compra.deliverer_id || '',
        
        // Footer
        id: compra.id,
        fecha_generacion: fechaStr,
        tipo_transito: esInternacional ? 'Tránsito Internacional (Importación)' : 'Tránsito Nacional (Envío local)'
      };
      
      console.log('Datos formateados para Nota de Entrega:', formattedData);
      setNotaData(formattedData);
      
      // Notificar que se imprimió (para cambiar estado)
      if (onImprimir) {
        onImprimir(compra.id);
      }
    } catch (err) {
      console.error('Error al formatear datos:', err);
      setError('Error al procesar los datos de la nota');
    } finally {
      setLoading(false);
    }
  }, [compra, onImprimir]);

  const handleIframeLoad = useCallback(() => {
    console.log('handleIframeLoad llamada - iframe:', !!iframeRef.current, 'data:', !!notaData);
    if (iframeRef.current && notaData) {
      try {
        console.log('Llamando renderNotaCompra con:', notaData);
        iframeRef.current.contentWindow.renderNotaCompra(notaData);
      } catch (err) {
        console.error('Error al inyectar datos en el Iframe:', err);
      }
    }
  }, [notaData]);

  // Si data se actualiza y el iframe ya cargó, forzar renderizado
  useEffect(() => {
    handleIframeLoad();
  }, [notaData, handleIframeLoad]);

  const handlePrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleDownload = () => {
    if (iframeRef.current) {
      if (typeof iframeRef.current.contentWindow.descargarPDF === 'function') {
        iframeRef.current.contentWindow.descargarPDF();
      } else {
        console.warn('La función descargarPDF no está disponible en el iframe.');
        iframeRef.current.contentWindow.print();
      }
    }
  };

  if (!compra) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 border-b p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <FileText className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Nota de Entrega</h2>
              <p className="text-sm text-blue-200">Documento oficial de entrega</p>
            </div>
          </div>
          <div className="flex gap-2">
            {!loading && !error && (
              <>
                <button 
                  onClick={handleDownload}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 font-medium flex items-center gap-2 transition-colors"
                >
                  <Download size={18} /> Descargar PDF
                </button>
                <button 
                  onClick={handlePrint}
                  className="px-4 py-2 bg-white text-blue-700 rounded-lg hover:bg-gray-100 font-medium flex items-center gap-2 transition-colors"
                >
                  <Printer size={18} /> Imprimir
                </button>
              </>
            )}
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 font-medium flex items-center gap-2 transition-colors"
            >
              <X size={18} /> Cerrar
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-gray-200 relative overflow-hidden flex justify-center items-center">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
              <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-gray-700">Generando Nota de Entrega...</p>
            </div>
          )}
          
          {error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <AlertCircle size={64} className="text-red-500 mb-4" />
              <p className="text-xl font-bold text-gray-800 mb-2">Error al cargar datos</p>
              <p className="text-gray-600 max-w-md text-center">{error}</p>
            </div>
          )}

          <iframe 
            ref={iframeRef}
            srcDoc={htmlContent}
            onLoad={handleIframeLoad}
            className={`w-full h-full bg-white transition-opacity duration-300 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
            title="Vista de impresión Nota de Entrega"
          />
        </div>

        {/* Footer */}
        <div className="bg-gray-100 border-t p-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
              📄 Nota de Entrega Generada
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Producto: <strong>{compra.producto}</strong> | ID: {compra.id}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotaCompraPrintModal;
